import { useState } from 'react';
import type { TimelineActie, TimelineEvent } from '../../features/car-detail/detail';

/**
 * The car's history, newest first.
 *
 * Entries we entered ourselves — a hand-typed odometer reading, a rate change —
 * carry a bin. Removing them here rather than from a separate screen keeps them
 * in the context that explains them: you see the reading next to the invoice it
 * contradicts, or the rate change next to the month it applies from.
 */
export function Timeline({
  events,
  onVerwijder,
}: {
  events: TimelineEvent[];
  onVerwijder?: (actie: TimelineActie) => void;
}) {
  if (!events.length) {
    return (
      <div style={{ fontSize: 13, color: '#888888' }}>
        Nog geen gebeurtenissen. Vul een aanschafdatum in of upload facturen.
      </div>
    );
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      {events.map((t, i) => (
        <div key={i} style={{ display: 'grid', gridTemplateColumns: '20px 1fr', gap: 16 }}>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <div
              style={{
                width: 14,
                height: 14,
                borderRadius: '50%',
                background: t.color,
                border: '2px solid #FBF5EE',
                boxShadow: `0 0 0 1px ${t.color}`,
                marginTop: 3,
              }}
            />
            <div style={{ flex: 1, width: 2, background: '#E3D9CC', margin: '2px 0' }} />
          </div>
          <div style={{ paddingBottom: 22, display: 'flex', alignItems: 'flex-start', gap: 10 }}>
            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#888888' }}>{t.date}</div>
              <div style={{ fontSize: 15, fontWeight: 900, color: '#111111', marginTop: 1 }}>
                {t.label}
              </div>
              <div style={{ fontSize: 13, color: '#666666', marginTop: 2 }}>{t.detail}</div>
            </div>
            {t.actie && onVerwijder && <Prullenbak actie={t.actie} onVerwijder={onVerwijder} />}
          </div>
        </div>
      ))}
    </div>
  );
}

function Prullenbak({
  actie,
  onVerwijder,
}: {
  actie: TimelineActie;
  onVerwijder: (actie: TimelineActie) => void;
}) {
  const [hover, setHover] = useState(false);
  return (
    <button
      type="button"
      onClick={() => onVerwijder(actie)}
      aria-label="Verwijderen"
      title="Verwijderen"
      onPointerEnter={() => setHover(true)}
      onPointerLeave={() => setHover(false)}
      style={{
        flex: 'none',
        width: 30,
        height: 30,
        boxSizing: 'border-box',
        padding: 0,
        lineHeight: 0,
        display: 'grid',
        placeItems: 'center',
        border: `1px solid ${hover ? '#B0543B' : 'transparent'}`,
        borderRadius: 2,
        background: hover ? '#FBF1EE' : 'transparent',
        color: hover ? '#B0543B' : '#C9BBA8',
        cursor: 'pointer',
        transition: 'color .15s ease, border-color .15s ease, background .15s ease',
      }}
    >
      <PrullenbakIcoon />
    </button>
  );
}

function PrullenbakIcoon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      style={{ display: 'block' }}
    >
      <path
        d="M4 7h16M10 4h4M6 7l1 13h10l1-13M10 11v6M14 11v6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
